/* eslint-disable @typescript-eslint/no-explicit-any */
import { useCookie } from '@/hooks/useCookie'
import { useFormik } from 'formik'
import { Fragment, useState } from 'react'

import ButtonLink from '@/components/ButtonLink'
import {
  FormButton,
  FormGroup,
  FormInput,
  FormText,
  FormTextRequire,
  FormView,
} from '@/components/Form'
import { FormCheckBoxSingle } from '@/components/Form/FormCheckBoxSingle'
import { HeaderLogo } from '@/components/HeaderLogo'
import PageNotification from '@/components/PageNotification/PageNotification'
import RegisterSlider from '@/components/RegisterSlider'
import { BannerType, DEFAULT_REGION, Languages } from '@/constants'
import { useNotification } from '@/containers'
import type { BannerList } from '@/types'
import { setContext } from '@/utils/api-interceptors'
import { getBanner, getConfig } from '@/utils/endpoint'
import { translateSchema } from '@/utils/validations'
import { Dialog, Transition } from '@headlessui/react'
import clsx from 'clsx'
import { getCookie, getCookies } from 'cookies-next'
import omit from 'lodash/omit'
import { type GetServerSideProps } from 'next'
import { useTranslations } from 'next-intl'
import Link from 'next/link'
import router from 'next/router'

type RegisterProps = {
  banner: BannerList[]
}

const Register = ({ banner }: RegisterProps) => {
  const t = useTranslations('Register')
  const { systemConfig } = useNotification()
  const [isSubmitted, setIsSubmitted] = useState<boolean>(false)
  const [errorMessage, setErrorMessage] = useState<string>('')
  const [isProcessing, setIsProcessing] = useState(false)
  const [, setRequestId] = useCookie('requestId', {})
  const [isSuccess, setIsSucces] = useState(false)
  const [dataProfile, setDataProfile] = useState<any>(null)

  const onSubmitFn = () => {
    setIsSubmitted(true)
  }

  const formik = useFormik({
    initialValues: {
      name: '',
      phone: '',
      provinceCode: '',
      districtCode: '',
      wardCode: '',
      address: '',
      preference: false,
    },
    validationSchema: translateSchema('register', t),

    onSubmit: async (values: {
      name: string
      phone: string
      provinceCode: string
      districtCode: string
      wardCode: string
      address: string
      preference: boolean
    }) => {
      if (!window.grecaptcha) {
        console.error('reCAPTCHA not yet loaded')
        return
      }
      const tokenReCaptcha = await window.grecaptcha.execute(
        process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY as string,
        { action: 'register' }
      )
      if (tokenReCaptcha) {
        setErrorMessage('')
        const res = await fetch(
          `${process.env.NEXT_PUBLIC_AUTH_API_URL}/api/auth/register`,
          {
            method: 'POST',
            body: JSON.stringify(omit(values, 'preference')),
            headers: {
              'Content-Type': 'application/json',
              'Accept-Language': getCookie('language') || Languages.VN,
              'Region-code': getCookie('area') || DEFAULT_REGION,
              recaptcha: tokenReCaptcha,
            } as any,
          }
        ).catch((e: Error) => {
          return e
        })
        if (res instanceof Error) {
          return res
        }
        const { ...data } = await res.json()
        setDataProfile(data)

        if (!data.statusCode) {
          setIsSucces(true)
          const { requestId } = data.data
          setRequestId(requestId)
          setIsProcessing(false)
        }

        if (data.validators) {
          const errors = data.validators
            .reduce((acc: any, cur: any) => {
              const { ...identifier } = cur.constraints
              return [
                ...acc,
                {
                  key: cur.property,
                  message: Object.values(identifier)[0],
                },
              ]
            }, [])
            .reduce(
              (obj: any, item: any) =>
                Object.assign(obj, { [item.key]: item.message }),
              {}
            )
          setIsProcessing(false)
          formik.setErrors(errors)
          return
        }

        setErrorMessage(data.message)
        setIsProcessing(false)
        setTimeout(() => {
          setErrorMessage('')
        }, 5000)
      }
    },
  })
  const closeModal = () => {
    router.push('/login')
  }
  return (
    <>
      <style global jsx>{`
        body::-webkit-scrollbar {
          display: none;
        }
      `}</style>
      <main>
        <div className='absolute inset-0 z-[-1] h-screen bg-opacity-50'>
          <RegisterSlider
            sliders={
              banner.find((e: BannerList) => e.type === BannerType.REGISTER)
                ?.items
            }
          />
        </div>
        <div
          className={`h-[calc(100vh - 129px)] m-auto flex max-w-[1200px] items-center pt-[5px] ${
            isSuccess ? 'mb-[60px] justify-center' : 'gap-x-[210px]'
          }`}
        >
          {isSuccess ? (
            <Transition appear show={isSuccess} as={Fragment}>
              <Dialog
                as='div'
                className='relative z-[9999]'
                onClose={() => closeModal()}
              >
                <Transition.Child
                  as={Fragment}
                  enter='ease-out duration-300'
                  enterFrom='opacity-0'
                  enterTo='opacity-100'
                  leave='ease-in duration-300'
                  leaveFrom='opacity-100'
                  leaveTo='opacity-0'
                >
                  <div className='fixed inset-0 bg-dark-900/50 transition-opacity' />
                </Transition.Child>
                <div className='fixed inset-0 z-[9999] overflow-y-auto'>
                  <div className='flex min-h-full items-center justify-center p-4 text-center'>
                    <Transition.Child
                      as={Fragment}
                      enter='ease-out duration-300'
                      enterFrom='opacity-0 scale-50'
                      enterTo='opacity-100 scale-100'
                      leave='ease-in duration-300'
                      leaveFrom='opacity-100 scale-100'
                      leaveTo='opacity-0 scale-50'
                    >
                      <Dialog.Panel className='relative h-[390px] w-[440px] max-w-full transform rounded-2xl bg-white p-1 text-left align-middle shadow-xl transition-all'>
                        <div className='modalContent flex flex-col items-center justify-center p-4'>
                          <button
                            className='close absolute right-3 top-[10px]'
                            onClick={() => closeModal()}
                          >
                            <svg
                              width={24}
                              height={24}
                              viewBox='0 0 24 24'
                              fill='none'
                              xmlns='http://www.w3.org/2000/svg'
                            >
                              <path
                                d='M20.4848 3.51429C18.8066 1.83618 16.6684 0.693416 14.3406 0.230492C12.0129 -0.232431 9.60016 0.00528063 7.40752 0.913568C5.21487 1.82186 3.3408 3.35993 2.02228 5.33329C0.703757 7.30665 0 9.62668 0 12C0 14.3733 0.703757 16.6934 2.02228 18.6667C3.3408 20.6401 5.21487 22.1782 7.40752 23.0864C9.60016 23.9947 12.0129 24.2324 14.3406 23.7695C16.6684 23.3066 18.8066 22.1638 20.4848 20.4857C21.5993 19.3714 22.4833 18.0485 23.0864 16.5925C23.6896 15.1365 24 13.576 24 12C24 10.424 23.6896 8.86351 23.0864 7.40752C22.4833 5.95154 21.5993 4.62861 20.4848 3.51429ZM16.2848 15.0343C16.3705 15.1145 16.4388 15.2114 16.4854 15.319C16.5321 15.4266 16.5562 15.5427 16.5562 15.66C16.5562 15.7773 16.5321 15.8934 16.4854 16.001C16.4388 16.1086 16.3705 16.2055 16.2848 16.2857C16.2048 16.3652 16.1098 16.428 16.0053 16.4707C15.9009 16.5133 15.7891 16.5349 15.6763 16.5343C15.4514 16.5333 15.2359 16.4441 15.0763 16.2857L11.9991 13.2086L8.96485 16.2857C8.8052 16.4441 8.58971 16.5333 8.36485 16.5343C8.25204 16.5349 8.14022 16.5133 8.03579 16.4707C7.93135 16.428 7.83637 16.3652 7.75628 16.2857C7.59663 16.1251 7.50703 15.9079 7.50703 15.6814C7.50703 15.455 7.59663 15.2377 7.75628 15.0771L10.7906 12L7.75628 8.96572C7.61585 8.80175 7.54248 8.59083 7.55081 8.3751C7.55914 8.15938 7.64857 7.95475 7.80122 7.80209C7.95388 7.64944 8.15851 7.56001 8.37423 7.55168C8.58995 7.54335 8.80088 7.61673 8.96485 7.75715L11.9991 10.7914L15.0334 7.75715C15.1128 7.67498 15.2075 7.60924 15.3123 7.5637C15.4171 7.51815 15.5298 7.49368 15.644 7.49169C15.7582 7.4897 15.8717 7.51023 15.978 7.5521C16.0843 7.59397 16.1812 7.65636 16.2634 7.73572C16.3456 7.81508 16.4113 7.90984 16.4569 8.0146C16.5024 8.11936 16.5269 8.23207 16.5289 8.34628C16.5309 8.4605 16.5103 8.57399 16.4685 8.68027C16.4266 8.78655 16.3642 8.88355 16.2848 8.96572L13.2077 12L16.2848 15.0343Z'
                                fill='black'
                              />
                            </svg>
                          </button>
                          <PageNotification
                            className={`mx-4`}
                            type={'page'}
                            isCanceled={false}
                          >
                            <span
                              className={`block text-left text-xl font-bold leading-6`}
                            >
                              Đăng ký thành viên thành công
                            </span>
                          </PageNotification>

                          <div className='mt-[5px] flex flex-col items-center'>
                            <div className='mb-1 h-full w-full rounded-lg bg-white'>
                              <h2
                                className={`mx-[6px] mb-2 pt-2 text-left text-base font-bold leading-5`}
                              >
                                Đăng nhập và trải nghiệm đặc quyền mua hàng tại
                                hệ thống của{' '}
                                {systemConfig?.websiteBrandName || ''}
                              </h2>
                              <h3 className='mx-[6px] mb-3 text-left text-lg font-bold'>
                                Thông tin đăng ký
                              </h3>
                              <div className='accountInfo mx-[6px] flex flex-col items-start justify-start'>
                                <div className='mb-1 flex w-full flex-nowrap'>
                                  <span className='mr-3 whitespace-nowrap text-base font-bold text-dark-400'>
                                    Họ và tên:
                                  </span>
                                  <span className='text-left text-base text-dark-500'>
                                    {dataProfile?.data?.profile?.name}
                                  </span>
                                </div>
                                <div className='mb-1 flex w-full flex-nowrap'>
                                  <span className='mr-3 whitespace-nowrap text-base font-bold text-dark-400'>
                                    Điện thoại:
                                  </span>
                                  <span className='text-left text-base text-dark-500'>
                                    {dataProfile?.data?.profile?.phone}
                                  </span>
                                </div>
                                <div className='mb-1 flex w-full flex-nowrap'>
                                  <span className='mr-3 whitespace-nowrap text-base font-bold text-dark-400'>
                                    Địa chỉ:
                                  </span>
                                  <span className='text-left text-base text-dark-500'>
                                    {[
                                      dataProfile?.data?.profile?.address,
                                      dataProfile?.data?.profile?.ward?.name,
                                      dataProfile?.data?.profile?.district
                                        ?.name,
                                      dataProfile?.data?.profile?.province
                                        ?.name,
                                    ]
                                      .filter(Boolean)
                                      .join(', ')}
                                  </span>
                                </div>
                              </div>
                            </div>
                            <ButtonLink
                              href='/login'
                              text={t('login')}
                              classes='text-white-400 text-fieldInput font-bold uppercase w-[400px] h-14'
                              className='mt-[30px] rounded bg-primary text-mainBody'
                            />
                          </div>
                        </div>
                      </Dialog.Panel>
                    </Transition.Child>
                  </div>
                </div>
              </Dialog>
            </Transition>
          ) : (
            <>
              <div className='mx-auto flex min-h-screen w-[850px] flex-col items-center justify-center'>
                <HeaderLogo />
                <div className='mt-10 w-full max-w-full rounded-lg bg-white p-8 shadow-md'>
                  <div className='mb-[20px] text-center text-HeadlineMedium font-extrabold text-[#090D14]'>
                    {t('loginAccount')}
                  </div>
                  <div className='flex justify-between'>
                    <FormView onSubmit={formik.handleSubmit} className='w-full'>
                      <div className='mb-6 grid w-full grid-cols-1 gap-x-6 gap-y-4 md:grid-cols-2'>
                        <FormGroup className='flex w-full flex-col'>
                          <FormText className='mb-2 text-left text-[15px] text-[#090D14]'>
                            {t('fullname')}
                          </FormText>
                          <FormInput
                            name='name'
                            placeholder={t('fullname2')}
                            value={formik.values.name}
                            isError={isSubmitted && formik.errors.name}
                            onChange={formik.handleChange}
                            alert
                            className={clsx(
                              isSubmitted &&
                                formik.errors.name &&
                                'border border-red-600',
                              '  !h-12 w-full rounded-[6px] border border-solid border-info-border text-left transition hover:cursor-pointer'
                            )}
                          />
                          {isSubmitted && formik.errors.name ? (
                            <FormTextRequire className='text-sm font-normal text-red-500'>
                              {formik.errors.name}
                            </FormTextRequire>
                          ) : null}
                        </FormGroup>

                        <FormGroup className='flex w-full flex-col'>
                          <FormText className='mb-2 text-left text-[15px] text-[#090D14]'>
                            {t('phone')}
                          </FormText>
                          <FormInput
                            name='phone'
                            placeholder={t('phonePld')}
                            value={formik.values.phone}
                            isError={isSubmitted && formik.errors.phone}
                            onChange={e => {
                              formik.handleChange({
                                target: {
                                  value: e.target.value
                                    .trim()
                                    .replace(/\D/, ''),
                                  name: 'phone',
                                },
                              })
                              setErrorMessage('')
                            }}
                            alert
                            className={clsx(
                              isSubmitted &&
                                formik.errors.phone &&
                                'border border-red-600',
                              '  !h-12 w-full rounded-[6px] border border-solid border-info-border text-left transition hover:cursor-pointer'
                            )}
                          />
                          {isSubmitted && formik.errors.phone ? (
                            <FormTextRequire className='text-sm font-normal text-red-500'>
                              {formik.errors.phone}
                            </FormTextRequire>
                          ) : null}
                        </FormGroup>
                      </div>

                      {errorMessage && isSubmitted ? (
                        <FormTextRequire className='mb-[15px] border-none pl-0 text-fieldInput text-red-500'>
                          {errorMessage}
                        </FormTextRequire>
                      ) : (
                        <></>
                      )}

                      <FormGroup className='mb-6'>
                        <div className='flex items-center'>
                          <FormCheckBoxSingle
                            onChange={formik.handleChange}
                            name='preference'
                            checked={formik.values.preference}
                          >
                            <div className='flex-1 cursor-pointer select-none text-sm text-black-400'>
                              {t('agreeWith')}{' '}
                              <Link
                                className='relative font-bold text-primary hover:underline'
                                href={systemConfig?.pagePolicy || ''}
                                target='blank'
                              >
                                {t('termsAndConditions')}
                              </Link>{' '}
                              {t('member', {
                                brand: systemConfig?.websiteBrandName,
                              })}
                            </div>
                          </FormCheckBoxSingle>
                        </div>

                        {isSubmitted && formik.errors.preference && (
                          <div className='mx-5 mt-2'>
                            <FormTextRequire className='text-base font-normal text-red-500'>
                              {formik.errors.preference}
                            </FormTextRequire>
                          </div>
                        )}
                      </FormGroup>

                      <FormButton
                        className='mt-2 h-14 w-full rounded-lg border bg-primary p-[10px] text-Headline font-semibold uppercase text-white-400 transition hover:bg-opacity-90 disabled:outline-none'
                        disabled={isProcessing}
                        isProcessing={isProcessing}
                        onClick={onSubmitFn}
                      >
                        {t('register')}
                      </FormButton>
                    </FormView>
                  </div>

                  <span className='mt-6 flex items-center justify-center text-sm text-black-400'>
                    {t('haveAccount')}{' '}
                    <Link
                      className='ml-2 text-primary hover:underline'
                      href='/login'
                    >
                      {t('login')}
                    </Link>
                  </span>
                </div>
              </div>
            </>
          )}
        </div>
      </main>
    </>
  )
}

export default Register

export const getServerSideProps: GetServerSideProps = async ({
  req,
  res,
  locale,
}) => {
  const { language, messages } = await setContext(getCookies({ req, res }), {
    language: locale,
  })
  const [banner, systemConfig] = await Promise.all([getBanner(), getConfig()])
  return {
    props: {
      messages,
      language,
      banner,
      systemConfig,
    },
  }
}
